<template>
  <div class="flex flex-1 items-center">
    <label for="search-field" class="sr-only">Search</label>
    <i
      class="fa-sharp fa-regular fa-magnifying-glass fa-fw pointer-events-none text-neutral-400"
      aria-hidden="true"></i>
    <div
      class="flex items-center h-full ml-3 w-full pr-0 text-neutral-400 hover:text-neutral-500 dark:hover:text-neutral-300 bg-transparent sm:text-sm cursor-text"
      @click="open = true">
      Search...
    </div>

    <TransitionRoot :show="open" as="template" @after-leave="query = ''" appear>
      <DialogDialog class="relative z-50" @close="open = false">
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-200"
          leave-from="opacity-100"
          leave-to="opacity-0">
          <div class="fixed inset-0 bg-neutral-900/85 transition-opacity" />
        </TransitionChild>

        <div class="fixed inset-0 z-10 w-screen overflow-y-auto p-4 sm:p-6 md:p-20">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95">
            <DialogPanel
              class="mx-auto max-w-xl transform divide-y divide-neutral-100 overflow-hidden rounded-xl bg-white dark:bg-neutral-700 dark:divide-neutral-500 shadow-2xl ring-1 ring-black/5 transition-all">
              <Combobox @update:modelValue="onSelect">
                <div class="relative">
                  <i
                    class="fa-sharp fa-regular fa-magnifying-glass fa-fw pointer-events-none text-neutral-400 absolute left-4 top-3.5"
                    aria-hidden="true"></i>
                  <ComboboxInput
                    class="h-12 w-full border-0 bg-transparent pl-11 pr-4 text-neutral-900 dark:text-white placeholder:text-neutral-400 focus:ring-0 sm:text-sm"
                    placeholder="Search..."
                    @change="query = $event.target.value"
                    @blur="query = query" />
                </div>

                <ComboboxOptions
                  v-if="results.length > 0"
                  static
                  class="max-h-96 transform-gpu scroll-py-3 overflow-y-auto p-3">
                  <ComboboxOption
                    v-for="item in results"
                    :key="item.id"
                    :value="item"
                    as="template"
                    v-slot="{ active }">
                    <li
                      :class="[
                        'flex cursor-pointer select-none rounded-xl p-3',
                        active && 'bg-neutral-100 dark:bg-neutral-600 outline-hidden'
                      ]">
                      <div
                        class="flex size-10 flex-none items-center justify-center rounded-lg bg-accent-500 text-accent-text">
                        <i class="fa-sharp fa-regular text-lg fa-fw" :class="item.icon" aria-hidden="true"></i>
                      </div>
                      <div class="ml-4 flex-auto min-w-0">
                        <p
                          :class="[
                            'text-sm font-semibold truncate',
                            active ? 'text-neutral-900 dark:text-white' : 'text-neutral-700 dark:text-neutral-300'
                          ]">
                          {{ item.name }}
                        </p>
                        <p
                          :class="[
                            'text-sm',
                            active ? 'text-neutral-700 dark:text-neutral-300' : 'text-neutral-500 dark:text-neutral-400'
                          ]">
                          {{ item.caption }}
                        </p>
                      </div>
                    </li>
                  </ComboboxOption>
                </ComboboxOptions>

                <div v-if="isEmpty" class="px-6 py-14 text-center text-sm sm:px-14">
                  <i
                    class="fa-sharp fa-regular fa-exclamation-circle text-lg fa-fw pointer-events-none text-neutral-400"
                    aria-hidden="true"></i>
                  <p class="mt-4 font-semibold text-neutral-900 dark:text-white">No results found</p>
                  <p class="mt-2 text-neutral-500 dark:text-neutral-300">
                    No results found for this search term. Please try again.
                  </p>
                </div>
              </Combobox>
            </DialogPanel>
          </TransitionChild>
        </div>
      </DialogDialog>
    </TransitionRoot>
  </div>
</template>

<script>
import { useAuthStore } from '@/stores/auth'
import { useSystemStore } from '@/stores/system'
import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Dialog as DialogDialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot
} from '@headlessui/vue'
import { debounce } from 'lodash'
import { mapState } from 'pinia'
import Str from 'laravel-js-str'

export default {
  components: {
    Combobox,
    ComboboxInput,
    ComboboxOption,
    ComboboxOptions,
    DialogDialog,
    DialogPanel,
    TransitionChild,
    TransitionRoot
  },
  data() {
    return {
      open: false,
      query: '',
      results: [],
      abortController: null,
      isEmpty: false
    }
  },
  computed: {
    ...mapState(useSystemStore, ['hub', 'routes']),
    ...mapState(useAuthStore, ['selectedOffice'])
  },
  methods: {
    search: debounce(async function () {
      this.abortController?.abort()
      if (!this.query) {
        this.results = []
        this.isEmpty = false
        return
      }

      this.abortController = new AbortController()
      const results = await this.$api.get('v3/search', {
        params: {
          q: this.query,
          members_hub_id: this.hub.id,
          office_id: this.selectedOffice.id
        },
        signal: this.abortController.signal
      })
      this.results = results.data
        .map((result) => {
          const pages = this.routes.filter(
            (r) =>
              (result.model_type === 'App\\Models\\MembersHubPage' && r.id === result.model_id) ||
              (result.metadata?.members_module_map
                ? result.metadata?.members_module_map.find(
                    (mm) => this.selectedOffice.id === mm.office_id && r.modules.find((m) => m.component === mm.module)
                  )
                : r.modules.find((m) => m.model === result.model_type))
          )

          let params = {}
          if (result.model_type !== 'App\\Models\\MembersHubPage') {
            let key = result.model_type.split('\\')
            key = Str.camel(key[key.length - 1])
            params[key] = result.model_id
          }

          return pages.map((p) => {
            let name = result.name
            if (pages.length > 1) {
              name = `${p.name} - ${name}`
            }
            for (let param in params) {
              name = name.replace(`{{${param}}}`, params[param])
            }

            // const openPages = this.$route.matched.map((m) => m.meta.id)
            return {
              ...result,
              name,
              icon: pages.length > 1 ? p.icon : result.icon,
              to: this.$router.resolve({
                name: `page${p.id}`,
                params
              })?.fullPath
            }
          })
        })
        .flat(1)

      this.isEmpty = this.results.length === 0
    }, 500),
    onSelect(item) {
      this.open = false
      if (item.to) {
        this.$router.push(item.to)
      }
    }
  },
  watch: {
    query() {
      this.search()
    }
  }
}
</script>
